import React from "react";
import { Box, Typography, Chip, useMediaQuery, useTheme } from "@mui/material";
import { Star } from "@mui/icons-material";
import { BaseBanner } from "./BaseBanner";

export interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  rating?: number;
  reviewCount?: number;
  discount?: number;
  badge?: string;
  href: string;
}

export interface BannerProps {
  products: Product[];
  autoSlide?: boolean;
  slideInterval?: number;
  showDots?: boolean;
  ctaText?: string;
  onProductClick?: (product: Product) => void;
  onCtaClick?: () => void;
}

export const Banner728x90: React.FC<BannerProps> = ({
  products,
  autoSlide = true,
  slideInterval = 5000,
  ctaText = "Shop Now",
  onProductClick,
  onCtaClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <BaseBanner
      products={products}
      width={728}
      height={90}
      autoSlide={autoSlide}
      slideInterval={slideInterval}
      showDots={false}
    >
      {(product) => (
        <Box
          onClick={() => onProductClick?.(product)}
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            color: "white",
            px: isMobile ? 1 : 5,
            gap: isMobile ? 1.5 : 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0,
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                fontSize: isMobile ? "1.2rem" : "1.5rem",
                color: "white",
                textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              }}
            >
              TEMU
            </Typography>
            <Chip
              label="Реклама"
              size="small"
              sx={{
                backgroundColor: "rgba(255,255,255,0.9)",
                color: "#ff6b35",
                fontSize: "0.6rem",
                height: 18,
                fontWeight: "bold",
              }}
            />
          </Box>

          <Box
            component="img"
            src={product.image}
            alt={product.name}
            sx={{
              width: isMobile ? 50 : 60,
              height: isMobile ? 50 : 60,
              objectFit: "cover",
              borderRadius: 1,
              backgroundColor: "white",
              flexShrink: 0,
            }}
          />

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "100%" }}>
              {/* Only show badge on desktop */}
              {!isMobile && product.badge && (
                <Chip
                  label={product.badge}
                  size="small"
                  sx={{
                    backgroundColor: "#ff1744",
                    color: "white",
                    fontSize: "0.6rem",
                    height: 16,
                    // mb: 0.5,
                  }}
                />
              )}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.75rem" : "0.9rem",
                  lineHeight: isMobile ? 1.1 : 1.2,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: isMobile ? 3 : 2,
                  WebkitBoxOrient: "vertical",
                  // mb: isMobile ? 0 : 0.5,
                }}
              >
                {product.name}
              </Typography>
              {/* Only show rating on desktop and if there's space */}
              {!isMobile && product.rating && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Star sx={{ fontSize: 12, color: "#ffd700" }} />
                  <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                    {product.rating} ({product.reviewCount})
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ textAlign: "center", flexShrink: 0 }}>
            {isMobile ? (
              // Mobile layout: Stack price and discount vertically
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1rem",
                  }}
                >
                  {product.price} ден
                </Typography>
                {product.discount && (
                  <Chip
                    label={`-${product.discount}%`}
                    size="small"
                    sx={{
                      backgroundColor: "#ff1744",
                      color: "white",
                      fontSize: "0.6rem",
                      height: 16,
                      fontWeight: "bold",
                    }}
                  />
                )}
              </Box>
            ) : (
              // Desktop layout: Price and discount side by side
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                    }}
                  >
                    {product.price} ден
                  </Typography>
                  {product.discount && (
                    <Chip
                      label={`-${product.discount}%`}
                      size="small"
                      sx={{
                        backgroundColor: "#ff1744",
                        color: "white",
                        fontSize: "0.6rem",
                        height: 16,
                        fontWeight: "bold",
                      }}
                    />
                  )}
                </Box>
                {/* Only show original price on desktop */}
                {product.originalPrice && (
                  <Typography
                    variant="caption"
                    sx={{
                      textDecoration: "line-through",
                      opacity: 0.8,
                      fontSize: "0.7rem",
                      display: "block",
                    }}
                  >
                    {product.originalPrice} ден
                  </Typography>
                )}
              </>
            )}
          </Box>

          <Box
            onClick={(e) => {
              e.stopPropagation();
              onCtaClick?.();
            }}
            sx={{
              backgroundColor: "white",
              color: "#ff6b35",
              px: isMobile ? 1.5 : 2,
              py: isMobile ? 0.8 : 1,
              borderRadius: 1,
              fontWeight: "bold",
              fontSize: isMobile ? "0.7rem" : "0.8rem",
              cursor: "pointer",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            {isMobile ? "Купи" : ctaText}
          </Box>
        </Box>
      )}
    </BaseBanner>
  );
};
